import {
  ActionIcon,
  Alert,
  Button,
  Card,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconAlertTriangle, IconPencil } from '@tabler/icons-react';
import { useState } from 'react';
import { useUpdateCase } from '../api/cases';
import { parseMutationError } from '../api/errors';
import { useHasPermission } from '../api/session';
import { ReasonConfirmModal } from './reason-confirm-modal';

interface Props {
  caseId: string;
  externalReference: string | null;
  clinicalContext: Record<string, unknown> | null;
  customFields: Record<string, string> | null;
}

/**
 * Inline-editable case fields (case-triage capability 1a). Only
 * external_reference is directly editable here; clinical_context and
 * custom_fields are structured product-specific JSON blobs shown
 * read-only, since a real inline JSON editor is out of scope for this
 * card (the API/hook already supports sending them if that need arises).
 */
export function CaseFieldsCard({
  caseId,
  externalReference,
  clinicalContext,
  customFields,
}: Props) {
  const canEdit = useHasPermission('case_triage:write');
  const updateCase = useUpdateCase(caseId);

  const [editingRef, setEditingRef] = useState(false);
  const [draftRef, setDraftRef] = useState(externalReference ?? '');
  const [pendingRef, setPendingRef] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function startEditRef() {
    setDraftRef(externalReference ?? '');
    setEditingRef(true);
    setError(null);
  }

  function saveRef() {
    setPendingRef(draftRef.trim());
    setEditingRef(false);
  }

  function handleConfirm(reason: string) {
    if (pendingRef === null) return;
    setError(null);
    updateCase.mutate(
      { externalReference: pendingRef, reason },
      {
        onSuccess: () => setPendingRef(null),
        onError: (err) => setError(parseMutationError(err, 'Could not update the case.')),
      },
    );
  }

  return (
    <Card withBorder padding="lg">
      <Stack gap="md">
        <Title order={5}>Case fields</Title>

        {error && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            {error}
          </Alert>
        )}

        {editingRef ? (
          <Group align="flex-end" gap="xs">
            <TextInput
              label="New external reference"
              value={draftRef}
              onChange={(e) => setDraftRef(e.currentTarget.value)}
              style={{ flex: 1 }}
            />
            <Button variant="default" onClick={() => setEditingRef(false)}>
              Cancel
            </Button>
            <Button onClick={saveRef}>Save</Button>
          </Group>
        ) : (
          <Group gap="xs" align="center">
            <Text size="xs" c="dimmed">
              External reference
            </Text>
            <Text>{externalReference ?? '—'}</Text>
            {canEdit && (
              <Tooltip label="Edit external reference">
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  aria-label="Edit external reference"
                  onClick={startEditRef}
                >
                  <IconPencil size={14} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        )}

        <Group gap="xs" align="flex-start">
          <Text size="xs" c="dimmed">
            Clinical context
          </Text>
          <Text size="sm" ff="monospace">
            {clinicalContext ? JSON.stringify(clinicalContext) : '—'}
          </Text>
        </Group>

        <Group gap="xs" align="flex-start">
          <Text size="xs" c="dimmed">
            Custom fields
          </Text>
          <Text size="sm" ff="monospace">
            {customFields ? JSON.stringify(customFields) : '—'}
          </Text>
        </Group>
      </Stack>

      <ReasonConfirmModal
        opened={pendingRef !== null}
        title="Confirm action"
        description={`Change external reference to "${pendingRef ?? ''}".`}
        confirmLabel="Confirm"
        isPending={updateCase.isPending}
        onConfirm={handleConfirm}
        onClose={() => {
          setPendingRef(null);
          setError(null);
        }}
      />
    </Card>
  );
}
